<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
// Bloco 3, "O que diz". Texto livre com variáveis, modelos da etapa, outras
// versões do texto e anexo; no WhatsApp oficial, modelo aprovado com parâmetros
// (reaproveitando o WhatsAppTemplateParser que já existia).
import { ref } from 'vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const props = defineProps({
  state: { type: Object, required: true },
  isFreeText: { type: Boolean, default: false },
  isWaha: { type: Boolean, default: false },
  templateOptions: { type: Array, default: () => [] },
  selectedTemplate: { type: Object, default: null },
  stageTemplates: { type: Array, default: () => [] },
  variableOptions: { type: Array, default: () => [] },
  parserRef: { type: Object, default: null },
});

const emit = defineEmits([
  'insertVariable',
  'addVariant',
  'removeVariant',
  'mediaUpload',
  'removeMedia',
  'variantMedia',
  'removeVariantMedia',
  'applyStageTemplate',
  'addTemplateVariant',
  'removeTemplateVariant',
]);

const templateParserRef = ref(null);
defineExpose({ templateParserRef });

// Escrito assim (e não interpolado no template) porque a chave dupla dentro da
// interpolação confunde o compilador de template do Vue.
const variableChip = name => '{' + name + '}';

const showVariables = ref(false);
const showStageTemplates = ref(false);
</script>

<template>
  <div class="flex flex-col gap-3">
    <template v-if="isFreeText">
      <div class="flex flex-col gap-1">
        <label for="campaign-message" class="text-sm font-medium text-n-slate-12">
          Mensagem
        </label>
        <textarea
          id="campaign-message"
          data-testid="campaign-message"
          v-model="state.wahaMessage"
          rows="4"
          class="w-full px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12"
          placeholder="Oi {primeiro_nome}, tudo bem? ..."
        />
      </div>

      <div class="flex flex-wrap gap-1.5">
        <button
          type="button"
          class="px-2.5 py-1 text-xs border rounded-full border-n-weak text-n-slate-11 hover:bg-n-alpha-black2"
          @click="showVariables = !showVariables"
        >
          + variável
        </button>
        <button
          v-if="stageTemplates.length"
          type="button"
          class="px-2.5 py-1 text-xs border rounded-full border-n-weak text-n-slate-11 hover:bg-n-alpha-black2"
          @click="showStageTemplates = !showStageTemplates"
        >
          Usar um modelo
        </button>
        <button
          v-if="state.messageVariants.length < 4"
          type="button"
          class="px-2.5 py-1 text-xs border rounded-full border-n-weak text-n-slate-11 hover:bg-n-alpha-black2"
          @click="emit('addVariant')"
        >
          + outra versão do texto
        </button>
        <label
          v-if="isWaha"
          class="px-2.5 py-1 text-xs border rounded-full cursor-pointer border-n-weak text-n-slate-11 hover:bg-n-alpha-black2"
        >
          + anexo
          <input type="file" class="hidden" @change="emit('mediaUpload', $event)" />
        </label>
      </div>

      <div v-if="showVariables" class="flex flex-wrap gap-1.5">
        <button
          v-for="variable in variableOptions"
          :key="variable"
          type="button"
          class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-black2 text-n-blue-11"
          @click="emit('insertVariable', variable)"
        >
          {{ variableChip(variable) }}
        </button>
      </div>

      <div v-if="showStageTemplates" class="flex flex-col gap-1">
        <button
          v-for="(template, idx) in stageTemplates"
          :key="idx"
          type="button"
          class="px-2 py-1 text-xs text-left rounded-md bg-n-alpha-black2 text-n-slate-11 hover:text-n-slate-12"
          @click="emit('applyStageTemplate', template)"
        >
          {{ template.title || template.content }}
        </button>
      </div>

      <p v-if="state.mediaUploading" class="text-xs text-n-slate-11">
        Enviando anexo...
      </p>
      <div
        v-else-if="state.attachedMedia"
        class="flex items-center gap-2 px-2 py-1 text-xs rounded-lg bg-n-alpha-black2 text-n-slate-11"
      >
        <span class="i-ph-paperclip size-3.5" />
        {{ state.attachedMedia.fileName }}
        <button type="button" class="hover:underline" @click="emit('removeMedia')">
          remover
        </button>
      </div>

      <div
        v-for="(variant, idx) in state.messageVariants"
        :key="idx"
        class="flex flex-col gap-1 p-2 border rounded-xl border-n-weak"
      >
        <div class="flex items-center justify-between">
          <span class="text-xs font-medium text-n-slate-11">
            Versão {{ String.fromCharCode(66 + idx) }}
          </span>
          <button
            type="button"
            class="text-xs text-n-slate-10 hover:text-n-ruby-11"
            @click="emit('removeVariant', idx)"
          >
            remover
          </button>
        </div>
        <textarea
          v-model="variant.text"
          rows="3"
          class="w-full px-3 py-2 text-sm border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12"
          placeholder="Outra forma de dizer a mesma coisa"
        />
        <div v-if="isWaha" class="flex items-center gap-2 text-xs text-n-slate-11">
          <label class="cursor-pointer hover:underline">
            + anexo
            <input type="file" class="hidden" @change="emit('variantMedia', idx, $event)" />
          </label>
          <template v-if="variant.media">
            <span>{{ variant.media.fileName }}</span>
            <button type="button" class="hover:underline" @click="emit('removeVariantMedia', idx)">
              remover
            </button>
          </template>
        </div>
      </div>

      <label class="flex items-center gap-2 text-xs text-n-slate-11">
        <input v-model="state.spintaxEnabled" type="checkbox" class="rounded" />
        Variar palavras automaticamente (spintax) para as mensagens não saírem
        idênticas
      </label>
    </template>

    <template v-else>
      <div class="flex flex-col gap-1">
        <label for="campaign-template" class="text-sm font-medium text-n-slate-12">
          Modelo aprovado
        </label>
        <ComboBox
          id="campaign-template"
          v-model="state.templateId"
          :options="templateOptions"
          placeholder="Escolha o modelo aprovado pela Meta"
          class="[&>div>button]:bg-n-alpha-black2"
        />
        <p v-if="!templateOptions.length" class="text-xs text-n-amber-11">
          Esta caixa ainda não tem modelo aprovado disponível.
        </p>
      </div>

      <WhatsAppTemplateParser
        v-if="selectedTemplate"
        ref="templateParserRef"
        :template="selectedTemplate"
        hide-media-input
      />

      <div v-if="state.templateId" class="flex flex-col gap-2">
        <div
          v-for="(variant, idx) in state.templateVariants"
          :key="idx"
          class="flex items-center gap-2"
        >
          <ComboBox
            v-model="variant.templateId"
            :options="templateOptions"
            placeholder="Outro modelo"
            class="flex-1 [&>div>button]:bg-n-alpha-black2"
          />
          <button
            type="button"
            class="text-xs text-n-slate-10 hover:text-n-ruby-11"
            @click="emit('removeTemplateVariant', idx)"
          >
            remover
          </button>
        </div>
        <button
          v-if="state.templateVariants.length < 4"
          type="button"
          class="self-start px-2.5 py-1 text-xs border rounded-full border-n-weak text-n-slate-11 hover:bg-n-alpha-black2"
          @click="emit('addTemplateVariant')"
        >
          + outra versão do texto
        </button>
      </div>
    </template>
  </div>
</template>
